import { ProductFormData, ProductRelatedAttributes } from "@/formTypes/ProductFormDataType";
import { FetchOpts, GenericModelApi } from "./GenericModelApi";
import { ApiClient, ApiError, ApiSchema, FetchResponse, NotFoundError } from "./ApiClient";
import { getTenantId } from "@utils/Auth";
import { AssetRelatedModelType, saveRelateAsset } from "./AssetsApi";
import { ProductFormDataKeys } from "@/generated/ProductFormDataKeys";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { ProductlogApi } from "./ProductlogApi";
import { SuscriptionDataType } from "./SuscriptionsApi";
import { ProductCostLogApi } from "@/api/ProductCostLogApi";
import { omitProperties } from "@utils/Utils";
import { CANT_EPSILON, roundCant } from "@utils/Cant";

export type ProductDataType = Partial<ApiSchema["Product"]["type"] & ProductFormData>;

const ASSET_INPUTS = {
    "imagen": "productImage"
}

class Api extends GenericModelApi<"Product", ProductDataType> {

    async fetchAll(filters: any = {}, token?: string, limit: number = 20, opts: FetchOpts = {}): Promise<FetchResponse<ProductDataType>> {
        this.apiMaxFetch = 300
        const selectionSet = [
            ...ProductFormDataKeys.filter(k => !ProductRelatedAttributes.includes(k)),
            "imagenAsset.*",
            "category.*",
        ]

        return super.fetchAll(filters, token, limit, selectionSet, opts);
    }

    /**
     * Productos del tenant con la seleccion minima que necesita la importacion masiva:
     * detectar duplicados, generar consecutivos y saber que existencias y costo tenia el
     * producto antes. `fetchAll` trae ademas la imagen y la categoria completas, y aqui se
     * recorren miles de productos.
     */
    async fetchAllForImport(token?: string): Promise<FetchResponse<ProductDataType>> {
        this.apiMaxFetch = 300
        const selectionSet = ["id", "codigo", "codigobarras", "descripcion_fact", "existencias", "costo", "categoryId"]

        return super.fetchAll({}, token, 1000, selectionSet, { noCache: true });
    }


    /**
     * Productos del tenant con la seleccion minima que necesita la auditoria de CAByS.
     *
     * `fetchAll` arrastra la imagen y la categoria completas, y aqui se recorre todo el
     * inventario solo para comparar dos campos.
     */
    async fetchAllForCabysAudit(token?: string): Promise<FetchResponse<ProductDataType>> {
        this.apiMaxFetch = 300
        const selectionSet = ["id", "codigo", "descripcion_fact", "codigo_cabys", "tipo_producto"]

        return super.fetchAll({}, token, 1000, selectionSet, { noCache: true });
    }


    async fetchAllByCategory(
        categoryId: string,
        filters: any = {},
        token?: string,
        limit: number = 1000,
        selectionSet?: any
    ): Promise<FetchResponse<ProductDataType>> {
        const tenantId = await getTenantId();

        const fetchFunction = ApiClient.models["Product"].listProductByCategory;
        const { data, errors, nextToken: nt } = await fetchFunction({
            owner: tenantId,
            categoryId: { eq: categoryId },
        }, {
            limit: limit,
            selectionSet: selectionSet,
            // Sin reenviar el token la consulta devuelve siempre la primera pagina,
            // lo que deja en bucle infinito a los reportes que paginan por categoria.
            nextToken: token || undefined,
        });

        if (errors) {
            throw new ApiError("Error al obtener el product por codigo", "API_ERROR", errors);
        }

        return { items: data || [], nextToken: nt }
    }

    async searchByProductByCode(
        productCode: string,
        selectionSet?: any,
        limit: number = 15
    ): Promise<FetchResponse<ProductDataType>> {
        const tenantId = await getTenantId();

        const fetchFunction = ApiClient.models[this.modelName][`list${this.modelName}ByTenant`];
        const { data, errors, nextToken: nt } = await fetchFunction({
            owner: tenantId,
            codigo: { beginsWith: productCode }
        }, {
            limit: limit,
            selectionSet: selectionSet,
        });

        if (errors) {
            throw new ApiError("Error al obtener el product por codigo", "API_ERROR", errors);
        }

        const pageItems = (data || [])
        return {
            items: pageItems,
            nextToken: nt
        }
    }

    async getProductByCodigoBarras(
        codigoBarras: string,
        selectionSet?: any
    ): Promise<ProductDataType> {
        const tenantId = await getTenantId();

        const collected: ProductDataType[] = [];

        const fetchFunction = ApiClient.models[this.modelName].listProductByCodigobarrasTenant;
        const { data, errors, nextToken: nt } = await fetchFunction({
            owner: tenantId,
            codigobarras: { eq: codigoBarras },
        }, {
            limit: 1,
            selectionSet: selectionSet,
        });

        if (errors) {
            throw new ApiError("Error al obtener el product por codigo de barras", "API_ERROR", errors);
        }

        const pageItems = (data || [])
        if (pageItems.length > 0) {
            return pageItems[0]
        }

        throw new NotFoundError(`No existe ningun producto con el codigo de barras ingresado`, "API_ERROR", errors);
    }

    async getProductByCode(
        productCode: string,
        selectionSet?: any
    ): Promise<ProductDataType> {
        const tenantId = await getTenantId();

        const collected: ProductDataType[] = [];

        const fetchFunction = ApiClient.models[this.modelName][`list${this.modelName}ByTenant`];
        const { data, errors, nextToken: nt } = await fetchFunction({
            owner: tenantId,
            codigo: { eq: productCode },
        }, {
            limit: 1,
            selectionSet: selectionSet,
        });

        if (errors) {
            throw new ApiError("Error al obtener el product por codigo", "API_ERROR", errors);
        }

        const pageItems = (data || [])
        if (pageItems.length > 0) {
            return pageItems[0]
        }

        throw new NotFoundError(`No existe ningun producto con el codigo ingresado`, "API_ERROR", errors);
    }

    async saveAssets(id: string, input: Partial<Omit<ProductDataType, 'id' | 'tenantId'>>) {
        const tenantId = await getTenantId();
        const assetPath = `suscription/${tenantId}/`;
        const { owner, ...rest } = input as any;
        let newData = {
            ...rest
        }

        for (const [key, value] of Object.entries(ASSET_INPUTS)) {
            const inputKey = key as keyof typeof input;
            const inputValue = input[inputKey]
            if (inputValue != null && Array.isArray(inputValue)) {
                newData = await saveRelateAsset(newData, inputKey, inputValue, assetPath, value as AssetRelatedModelType, id)
            }
        }
        return newData;
    }

    async update(id: string, input: Partial<Omit<ProductDataType, 'id' | 'tenantId'>>): Promise<ProductDataType> {
        let newData = await this.saveAssets(id, input);
        if (!newData.codigobarras) {
            newData = {
                ...newData,
                codigobarras: null
            }
        }

        const { data, errors } = await ApiClient.models.Product.update({ id, ...newData });

        if (errors) {
            throw new ApiError(`Errores al actualizar Product`, "API_ERROR", errors);
        }

        return data || {} as ProductDataType;
    }
    /**
     * Update parcial de la clasificacion fiscal.
     *
     * No pasa por `update`: ese override normaliza `codigobarras` a null cuando el input
     * no lo trae, asi que un update parcial le borraria el codigo de barras al producto.
     */
    async updateCabysFields(id: string, input: { codigo_cabys: string, tipo_producto: number }): Promise<ProductDataType> {
        const { data, errors } = await ApiClient.models.Product.update({ id, ...input });

        if (errors) {
            throw new ApiError(`Errores al actualizar el CAByS del Product`, "API_ERROR", errors);
        }

        this.clearCache()

        return data || {} as ProductDataType;
    }

    async updateProductCostIfDifferent(providerId: string, productId: string, costo: number) {
        const previosProd = await this.get(productId);
        if (Number(previosProd.costo || 0) != costo) {
            await this.update(productId, { costo })
            await ProductCostLogApi.createProductCostLog(providerId, productId, costo)
        }
    }

    async reduceStock(products: EDocumentProductDataType[], comments: string = "") {
        return await this.stockAdjustment(products, comments, false)
    }

    async increaseStock(products: EDocumentProductDataType[], comments: string = "") {
        return await this.stockAdjustment(products, comments, true)
    }

    async stockAdjustment(products: EDocumentProductDataType[], comments: string = "", isIncrease: boolean = true) {
        const changeFactor = isIncrease ? 1 : -1;

        const updatePromises = products.filter(p => p.productId).flatMap(async (p) => {
            const promises = []
            const selectionSet = [
                ...ProductFormDataKeys.filter(k => !ProductRelatedAttributes.includes(k)),
                "category.*",
            ]

            const product = await this.get(`${p.productId}`, { selectionSet })
            const isCategoryWithoutInventory = product.category.sin_inventario || false
            if (product?.id) {
                const cant = roundCant(changeFactor * Number(p.cant || 0))
                const previousAmount = Number(product.existencias || 0)
                let newAmount = roundCant(previousAmount + cant);
                if (isCategoryWithoutInventory) {
                    newAmount = 0;
                }
                promises.push(this.update(product.id, {
                    existencias: newAmount
                }))
                promises.push(ProductlogApi.createProductLog(previousAmount, cant, newAmount, product.id, comments))
            }

            return promises;

        })
        return await Promise.all(updatePromises)
    }

    async checkStock(products: EDocumentProductDataType[], suscriptionData: SuscriptionDataType): Promise<EDocumentProductDataType[]> {
        const { inventario_negativo } = suscriptionData
        if (inventario_negativo) {
            return []
        } else {
            // return a list of products with Low Inventory 
            const selectionSet = [
                ...ProductFormDataKeys.filter(k => !ProductRelatedAttributes.includes(k)),
                "category.*",
            ]
            const lowInventoryProduct = [];
            for (const p of products) {
                if (!p.productId) continue;

                const product = await this.get(String(p.productId), { selectionSet });
                if (!product) continue;

                const { category } = product;
                if (category?.sin_inventario) continue;
                if (Number(product.existencias) + CANT_EPSILON >= Number(p.cant)) continue;

                lowInventoryProduct.push(p);
            }

            return lowInventoryProduct
        }
    }

}
export const ProductApi = new Api("Product", "codigo")